import React, { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { Sidebar } from './Sidebar'
import { UploadScopeModal } from './modals/UploadScopeModal'
import { useCreateProject } from '../lib/hooks'
import { projectsApi, filesApi, type ProjectCreateRequest } from '../lib/api'
import toast from 'react-hot-toast'

export const PropalystLayout: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showUploadScope, setShowUploadScope] = useState(false)
  const [isCreatingProject, setIsCreatingProject] = useState(false)
  const [draggedFiles, setDraggedFiles] = useState<File[]>([])
  const createProjectMutation = useCreateProject()

  const handleCreateProject = async (data: { projectName: string; files: File[] }) => {
    if (isCreatingProject) return
    
    setIsCreatingProject(true)
    
    try {
      // Step 1: Upload the first file (RFP document)
      if (data.files.length === 0) {
        toast.error('Please select at least one file')
        return
      }

      const mainFile = data.files[0] // Use the first file as the main RFP document
      const formData = new FormData()
      formData.append('file', mainFile)
      formData.append('title', data.projectName)
      formData.append('description', `Project created from RFP: ${mainFile.name}`)
      formData.append('auto_evaluate', 'true')
      formData.append('cover_letter_only', data.coverLetterOnly.toString())
      // notification_emails can be empty, so we don't need to append it

      toast.loading('Creating project with RFP document...', { id: 'upload' })
      
      // Use the async endpoint that creates project + uploads RFP in background
      const response = await projectsApi.createWithRfpAsync(formData)
      
      toast.dismiss('upload')
      toast.success('Project created! RFP file is being processed in background.')
      
      // Invalidate projects query to update the sidebar immediately
      queryClient.invalidateQueries(['projects'])
      
      // Reset form and close modal
      setShowUploadScope(false)
      
      // Navigate to the new project
      navigate(`/projects/${response.project.id}`)
      
    } catch (error: any) {
      console.error('Error creating project:', error)
      toast.error(error.response?.data?.message || 'Failed to create project')
    } finally {
      setIsCreatingProject(false)
    }
  }

  const handleDraggedFilesUpload = (files: File[]) => {
    setDraggedFiles(files)
    setShowUploadScope(true)
  }

  const handleModalClose = () => {
    setShowUploadScope(false)
    setDraggedFiles([]) // Clear dragged files when modal closes
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <Sidebar onCreateProject={() => setShowUploadScope(true)} />
      <div className="flex-1 overflow-y-auto">
        <Outlet context={{ setShowUploadScope, handleDraggedFilesUpload }} />
      </div>
      
      {/* Upload Scope Modal */}
      <UploadScopeModal
        isOpen={showUploadScope}
        onClose={handleModalClose}
        onSubmit={handleCreateProject}
        isLoading={isCreatingProject}
        initialFiles={draggedFiles}
      />
      
    </div>
  )
}

export default PropalystLayout